import React, { useReducer } from "react";
import dayjs from "dayjs";
import { Grid, IconButton, Popover, Typography } from "@mui/material";
import { useIntl } from "react-intl";
import PopupState, { bindTrigger, bindPopover } from "material-ui-popup-state";

import style from "./index.module.css";

import GetImages from "@/components/GetImages";
import Confirmation from "../Confirmation";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchDoctorReviewRatingList } from "@/store/slices/doctor/profile";

import { transformDateToMMDDYYY } from "@/utils";
import { storage } from "@/utils/Storage";
import {
  postReplyToPatientReviewApi,
  removeReplyOrReviewDoctorApi,
} from "@/utils/apis/doctor/profile";
import useIsTablet from "@/utils/hooks/useIsTablet";
import useIsMobile from "@/utils/hooks/useIsMobile";
import { usePermissionRestriction } from "@/utils/hooks/usePermissionRestriction";

import { IRoleType } from "@/models/role";
import {
  IModalKeyEnums,
  IDoctorReviewPermissionEnums,
} from "@/models/permission";
import { IRemoveReviewActionType, ISessionKeys } from "@/models";

const initialState = {
  deleteReviewConfirmation: false,
  showReplySection: null,
  reviewId: null,
};

type IState = typeof initialState;

enum IDoctorReviewsActionType {
  SET_DELETE_REVIEW_CONFIRMATION = "SET_DELETE_REVIEW_CONFIRMATION",
  SET_SHOW_REPLY_SECTION = "SET_SHOW_REPLY_SECTION",
  SET_REVIEW_ID = "SET_REVIEW_ID",
}

type IDoctorReviewsAction =
  | {
      type: IDoctorReviewsActionType.SET_DELETE_REVIEW_CONFIRMATION;
      payload: boolean;
    }
  | {
      type: IDoctorReviewsActionType.SET_SHOW_REPLY_SECTION;
      payload: number | null;
    }
  | {
      type: IDoctorReviewsActionType.SET_REVIEW_ID;
      payload: string | null;
    };

const reducer = (state: IState, action: IDoctorReviewsAction): IState => {
  switch (action.type) {
    case IDoctorReviewsActionType.SET_DELETE_REVIEW_CONFIRMATION:
      return { ...state, deleteReviewConfirmation: action.payload };
    case IDoctorReviewsActionType.SET_SHOW_REPLY_SECTION:
      return { ...state, showReplySection: action.payload };
    case IDoctorReviewsActionType.SET_REVIEW_ID:
      return { ...state, reviewId: action.payload };
    default:
      return state;
  }
};

const DoctorReviews: React.FC = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { deleteReviewConfirmation, showReplySection, reviewId } = state;

  const { isTablet } = useIsTablet();
  const { isMobile } = useIsMobile();
  const doctorProile = useAppSelector((state) => state.doctorProfile);

  const appDispatch = useAppDispatch();
  const permissionRestrictionHandler = usePermissionRestriction();
  const intl = useIntl();

  const roleType = storage.getSessionToken(ISessionKeys.ROLE_TYPE);

  const doctorOrJrDoctor =
    roleType === IRoleType.DOCTOR || roleType === IRoleType.JR_DOCTOR;

  const replyToReviewHandler = async (event: any, reviewId: string) => {
    try {
      if (event.key === "Enter") {
        await postReplyToPatientReviewApi({
          reply: event.target.value,
          reviewId,
        });
        dispatch({
          type: IDoctorReviewsActionType.SET_SHOW_REPLY_SECTION,
          payload: null,
        });
        appDispatch(fetchDoctorReviewRatingList({}));
      }
    } catch (error) {
      console.log(error);
    }
  };

  const replyToPatientReviewHandler = (indexValue: number) => {
    const isPermissionGranted = permissionRestrictionHandler(
      IModalKeyEnums.DOCTOR_REVIEW,
      IDoctorReviewPermissionEnums.REPLY_REVIEWS
    );
    if (!isPermissionGranted) return;
    dispatch({
      type: IDoctorReviewsActionType.SET_SHOW_REPLY_SECTION,
      payload: indexValue,
    });
  };

  const removeReviewHandler = async (
    reviewId: string,
    action: IRemoveReviewActionType
  ) => {
    try {
      const isPermissionGranted = permissionRestrictionHandler(
        IModalKeyEnums.DOCTOR_REVIEW,
        IDoctorReviewPermissionEnums.DELETE_REVIEW
      );
      if (!isPermissionGranted) return;
      const response = await removeReplyOrReviewDoctorApi(reviewId, action);
      if ("result" in response) {
        appDispatch(fetchDoctorReviewRatingList({}));
      }
    } catch (error) {
      console.log(error);
    }
  };

  const deleteReviewConfirmationHandler = () =>
    dispatch({
      type: IDoctorReviewsActionType.SET_DELETE_REVIEW_CONFIRMATION,
      payload: !deleteReviewConfirmation,
    });

  const deleteReviewHandler = (reviewId: string) => {
    dispatch({
      type: IDoctorReviewsActionType.SET_REVIEW_ID,
      payload: reviewId,
    });
    deleteReviewConfirmationHandler();
  };

  return (
    <Grid item>
      {doctorProile?.ratingReviewList?.review?.length > 0 ? (
        doctorProile?.ratingReviewList?.review?.map((ratingList, index) => (
          <Grid
            item
            sx={{
              backgroundColor:
                doctorOrJrDoctor && !ratingList.doctor_reply
                  ? "var(--warm-neutral-50)"
                  : "var(--neutral-100)",
            }}
            className={style.container}
            key={`review-${ratingList.id}-${index}`}
          >
            <Grid container>
              <Grid item xs={12} className={style.reviewerReplyHeader}>
                <Typography component={"h6"}>{ratingList.name}</Typography>
                <Typography
                  component={"p"}
                  display={"flex"}
                  gap={1}
                  alignItems={"center"}
                >
                  {transformDateToMMDDYYY(ratingList?.createdAt)}
                  {doctorOrJrDoctor && (
                    <IconButton
                      onClick={() => deleteReviewHandler(ratingList?.id)}
                    >
                      <GetImages
                        name="TrashReviewIcon"
                        width="24"
                        height="24"
                      />
                    </IconButton>
                  )}
                </Typography>
              </Grid>
              <Grid item xs={12} className={style.reviewStarContainer}>
                {Array.from({ length: ratingList?.no_of_stars }).map((_, i) => (
                  <GetImages
                    key={`yellow-star-${ratingList.id}-${i}`}
                    name="YellowStarSmallIcon"
                    width="16"
                    height="16"
                  />
                ))}

                {ratingList?.no_of_stars !== 5 &&
                  Array.from({ length: 5 - ratingList?.no_of_stars }).map(
                    (_, i) => (
                      <GetImages
                        key={`grey-star-${ratingList.id}-${i}`}
                        name="GreyedStarSmallIcon"
                        width="16"
                        height="16"
                      />
                    )
                  )}
              </Grid>
              <Grid item xs={12}>
                <Typography component={"p"} className={style.reviewerText}>
                  {ratingList?.review_message}
                </Typography>
                {!ratingList?.doctor_reply && doctorOrJrDoctor && (
                  <>
                    {index !== showReplySection && (
                      <Typography
                        component={"p"}
                        className={style.replayToReview}
                        sx={{
                          textAlign: isMobile || isTablet ? "right" : "left",
                        }}
                        onClick={() => replyToPatientReviewHandler(index)}
                      >
                        {intl.formatMessage({
                          id: "reply",
                          defaultMessage: "Reply",
                        })}
                      </Typography>
                    )}
                    {index === showReplySection && (
                      <Grid item className={style.replyContainer}>
                        <Grid item>
                          <Grid item className={style.reviewerReplyHeader}>
                            <Typography component={"h6"}>
                              {doctorProile?.detail?.name}
                            </Typography>
                            <Typography component={"p"}>
                              {transformDateToMMDDYYY(dayjs())}
                            </Typography>
                          </Grid>
                          <Typography
                            component={"p"}
                            className={style.repliedMessageContainer}
                          >
                            <input
                              type="text"
                              placeholder="Type here"
                              onKeyDown={(event) =>
                                replyToReviewHandler(event, ratingList?.id)
                              }
                              style={{
                                backgroundColor: "var(--warm-neutral-50)",
                              }}
                              autoFocus
                            />
                          </Typography>
                        </Grid>
                      </Grid>
                    )}
                  </>
                )}
                {ratingList?.doctor_reply && (
                  <Grid item className={style.replyContainer}>
                    <Grid item>
                      <Grid item className={style.reviewerReplyHeader}>
                        <Typography component={"h6"}>
                          {doctorProile?.detail?.name}
                        </Typography>
                        <Typography
                          component={"p"}
                          display={"flex"}
                          gap={1}
                          alignItems={"center"}
                        >
                          {transformDateToMMDDYYY(ratingList?.updatedAt)}
                          {doctorOrJrDoctor && (
                            <PopupState
                              variant="popover"
                              popupId="demo-popup-popover"
                            >
                              {(popupState) => (
                                <div>
                                  <IconButton {...bindTrigger(popupState)}>
                                    <GetImages
                                      name="RemoveReviewIcon"
                                      width="16"
                                      height="16"
                                    />
                                  </IconButton>

                                  <Popover
                                    {...bindPopover(popupState)}
                                    anchorOrigin={{
                                      vertical: "bottom",
                                      horizontal: "center",
                                    }}
                                    transformOrigin={{
                                      vertical: "top",
                                      horizontal: "center",
                                    }}
                                  >
                                    <Typography
                                      component={"p"}
                                      p={1}
                                      onClick={() =>
                                        removeReviewHandler(
                                          ratingList?.id,
                                          IRemoveReviewActionType.REMOVE_REPLY
                                        )
                                      }
                                    >
                                      {intl.formatMessage({
                                        id: "delete_reply",
                                        defaultMessage: "Delete Reply",
                                      })}
                                    </Typography>
                                  </Popover>
                                </div>
                              )}
                            </PopupState>
                          )}
                        </Typography>
                      </Grid>
                      <Typography
                        component={"p"}
                        className={style.repliedMessageContainer}
                      >
                        {ratingList?.doctor_reply}
                      </Typography>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        ))
      ) : (
        <Typography component={"h1"} textAlign={"center"} padding={"3rem"}>
          {intl.formatMessage({
            id: "no_patient_reviews",
            defaultMessage: "No Patient reviews",
          })}
        </Typography>
      )}
      {deleteReviewConfirmation && (
        <Confirmation
          title={"delete_review"}
          alterMessage={"delete_review_alter_message"}
          isOpen={deleteReviewConfirmation}
          closeHandler={deleteReviewConfirmationHandler}
          actionHandler={() =>
            removeReviewHandler(reviewId, IRemoveReviewActionType.REMOVE_REVIEW)
          }
        />
      )}
    </Grid>
  );
};

export default DoctorReviews;
